import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import omit from 'lodash/omit';
import mapValues from 'lodash/mapValues';
import cx from 'classnames';

import Spinner from 'components/spinner';
import BulletStepBar from 'components/bullet-step-bar';
import { Establishment } from 'graphql-api';
import { saveEstablishment } from 'api/establishments';
import { EstablishmentDataInterface } from 'types/establishment';

function EstablishmentForm({
  establishment,
  index,
  numberOfEstablishments,
  handlePrevious,
  handleNext
}: {
  establishment: Establishment;
  index: number;
  numberOfEstablishments: number;
  handlePrevious: Function;
  handleNext: Function;
}): JSX.Element {
  const [processing, setProcessing] = useState(false);
  const formattedEstablishmentData = mapValues(
    omit(establishment, ['createdAt', 'updatedAt', 'owner']),
    (value) => value?.toString()
  );
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<EstablishmentDataInterface>({
    defaultValues: formattedEstablishmentData
  });

  const onSubmit: SubmitHandler<EstablishmentDataInterface> = async (data) => {
    setProcessing(true);
    const updatedEstablishment = await saveEstablishment(data);
    setProcessing(false);
    handleNext(updatedEstablishment);
  };

  return (
    <form
      onSubmit={(...args) => {
        void handleSubmit(onSubmit)(...args);
      }}>
      <div className="bg-white mt-14 text-xs pt-5 shadow border border-gray-100">
        <div className="md:grid md:grid-cols-3 md:gap-6 px-6 py-5">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Establishment {index + 1}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{establishment.fullAddress}</p>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div>
              <label
                htmlFor="establishment-name"
                className="block text-sm font-medium text-gray-900">
                Establishment name
              </label>
              <div className="mb-4 mt-2 text-sm text-gray-700">
                Enter the name of the establishment. E.g. Acme Water Works - Inc - Station 1000
                (AB66242)
              </div>
              <div className="w-80 mb-4">
                <input
                  id="establishment-name"
                  type="text"
                  className={cx('input', { error: errors.establishmentName })}
                  placeholder="Establishment name"
                  {...register('establishmentName' as const, { required: 'Name is required' })}
                />
                {errors.establishmentName != null && (
                  <p className="error-message">{errors.establishmentName?.message}</p>
                )}
              </div>
            </div>
            <div className="mt-8">
              <label htmlFor="naics-code" className="block text-sm font-medium text-gray-900">
                Your NAICS code
              </label>
              <div className="mb-4 mt-2 text-sm text-gray-700">
                Enter the North American Industry Classification System code (NAICS) of the
                establishment. See{' '}
                <a
                  href="https://www.naics.com/search/"
                  target="_blank"
                  className="btn-link"
                  rel="noreferrer">
                  www.naics.com/search
                </a>
              </div>
              <div className="w-80 mb-4">
                <input
                  id="naics-code"
                  type="text"
                  className={cx('input', { error: errors.naicsCode })}
                  placeholder="NAICS code"
                  {...register('naicsCode' as const, { required: 'NAICS code is required' })}
                />
                {errors.naicsCode != null && (
                  <p className="error-message">{errors.naicsCode?.message}</p>
                )}
              </div>
            </div>
            <div className="mt-8 mb-4">
              <label htmlFor="major-activity" className="block text-sm font-medium text-gray-900">
                Major Activity
              </label>
              <div className="mb-4 mt-2 text-sm text-gray-700">
                Describe the major activity or activities of the establishment. Include the specific
                type of product or type of service provided, as well as the principal business or
                industrial activity. Be specific i.e. manufacturing steel casings, retail grocer,
                wholesale plumbing supplies, title insurance, etc.
              </div>
              <textarea
                id="major-activity"
                className={cx('input', { error: errors.naicsCode })}
                placeholder="Describe activity here..."
                {...register('majorActivity' as const, { required: 'Major Activity is required' })}
              />
              {errors.majorActivity != null && (
                <p className="error-message">{errors.majorActivity?.message}</p>
              )}
            </div>
            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-900">
                Was a California Pay Data Report filed for this establishment last year?
              </label>
              <div className="mb-4 mt-2 text-sm text-gray-700">
                Select &quot;Yes&quot; if the employer submitted a California Pay Data Report in the
                year prior to the current Reporting Year. Select &quot;No&quot; if the employer did
                not submit a California Pay Data Report in the year prior to the current Reporting
                Year. For the report due to DFEH by April 1, 2022 (covering the 2021 Reporting
                Year), the entry will be &quot;No&quot; for all employers.
              </div>
              <div className="mb-4">
                <div className="radio-container">
                  <input
                    className="radio"
                    type="radio"
                    value="true"
                    id="hasCPDRReportLastYearYes"
                    {...register('hasCPDRReportLastYear' as const, {
                      required: 'Please choose one option'
                    })}
                  />
                  <label className="radio-label" htmlFor="hasCPDRReportLastYearYes">
                    Yes
                  </label>
                </div>
                <div className="radio-container">
                  <input
                    className="radio"
                    type="radio"
                    value="false"
                    id="hasCPDRReportLastYearNo"
                    {...register('hasCPDRReportLastYear' as const, {
                      required: 'Please choose one option'
                    })}
                  />
                  <label className="radio-label" htmlFor="hasCPDRReportLastYearNo">
                    No
                  </label>
                </div>
                {errors.hasCPDRReportLastYear != null && (
                  <p className="error-message">{errors.hasCPDRReportLastYear?.message}</p>
                )}
              </div>
            </div>
            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-900">
                Was an EEO-1 Report filed for this establishment last year?
              </label>
              <div className="mb-4 mt-2 text-sm text-gray-700">
                Select &quot;Yes&quot; if the employer submitted any type of federal EEO-1 report in
                the year prior to the current Reporting Year for this establishment. Enter
                &quot;No&quot; if the employer did not submit any type of federal EEO-1 report in
                the year prior to the current Reporting Year for this establishment.
              </div>
              <div className="my-4">
                <div className="radio-container">
                  <input
                    className="radio"
                    type="radio"
                    value="true"
                    id="hasEEO1ReportLastYearYes"
                    {...register('hasEEO1ReportLastYear' as const, {
                      required: 'Please choose one option'
                    })}
                  />
                  <label className="radio-label" htmlFor="hasEEO1ReportLastYearYes">
                    Yes
                  </label>
                </div>
                <div className="radio-container">
                  <input
                    className="radio"
                    type="radio"
                    value="false"
                    id="hasEEO1ReportLastYearNo"
                    {...register('hasEEO1ReportLastYear' as const, {
                      required: 'Please choose one option'
                    })}
                  />
                  <label className="radio-label" htmlFor="hasEEO1ReportLastYearNo">
                    No
                  </label>
                </div>
                {errors.hasEEO1ReportLastYear != null && (
                  <p className="error-message">{errors.hasEEO1ReportLastYear?.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center bg-gray-100 py-3 px-6">
          <div className="flex flex-1">
            {index > 0 && (
              <a onClick={() => handlePrevious()} className="btn-secondary cursor-pointer">
                Previous establishment
              </a>
            )}
          </div>
          <BulletStepBar
            numberOfSteps={numberOfEstablishments}
            currentStep={index + 1}
            label="Establishment"
          />
          <div className="flex flex-1 justify-end">
            <button className="btn-primary cursor-pointer">
              {index === numberOfEstablishments - 1
                ? 'Next step: Title Mapping'
                : 'Next establishment'}
              {processing && <Spinner className="ml-2" />}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default EstablishmentForm;
